import { CognitoService } from './../service/cognito.service';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, combineLatest, map, startWith, Subscription } from 'rxjs';
import { MaintenanceService, JoinedLog } from '../service/maintenance.service';
import { ThemeService } from '../service/theme.service';
import { environment } from '../environments/environment';

type SortKey = 'thing_name' | 'timestamp';
type SortDir = 'asc' | 'desc';

@Component({
    selector: 'app-maintenance',
    templateUrl: './maintenance.component.html',
    styleUrls: ['./maintenance.component.css', '../../global-elements.css'],
    standalone: false
})
export class MaintenanceComponent implements OnInit, OnDestroy {
  // ---------- state ----------
  loading = false;
  errorMsg = '';

  // latest rows from API
  private latestLogsSubject = new BehaviorSubject<JoinedLog[]>([]);
  latestLogs$ = this.latestLogsSubject.asObservable();

  // filters
  filterForm = this.fb.group({
    thingQuery: [''],
    fromEpoch: [null as number | null],
    toEpoch:   [null as number | null],
  });

  // sorting
  private sortKeySubject = new BehaviorSubject<SortKey>('thing_name');
  private sortDirSubject = new BehaviorSubject<SortDir>('asc');
  sortKey$ = this.sortKeySubject.asObservable();
  sortDir$ = this.sortDirSubject.asObservable();

  // derived view rows
  viewRows$ = combineLatest([
    this.latestLogs$,
    this.filterForm.valueChanges.pipe(startWith(this.filterForm.value)),
    this.sortKey$,
    this.sortDir$
  ]).pipe(
    map(([rows, filters, sortKey, sortDir]) => {
      const q = (filters.thingQuery ?? '').toString().trim().toLowerCase();
      const from = filters.fromEpoch ?? null;
      const to   = filters.toEpoch ?? null;

      let out = rows.filter(r => {
        const matchesName = q ? r.thing_name.toLowerCase().includes(q) : true;
        const gteFrom = from != null ? r.timestamp >= from : true;
        const lteTo   = to   != null ? r.timestamp <= to   : true;
        return matchesName && gteFrom && lteTo;
      });

      out = out.slice().sort((a, b) => {
        const aVal = sortKey === 'thing_name' ? a.thing_name.toLowerCase() : a.timestamp;
        const bVal = sortKey === 'thing_name' ? b.thing_name.toLowerCase() : b.timestamp;
        const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
        return sortDir === 'asc' ? cmp : -cmp;
      });

      return out;
    })
  );

  // ---------- details modal ----------
  showDetailsModal = false;
  detailsThingName = '';
  detailsLoading = false;
  detailsLogs: JoinedLog[] = [];

  // ---------- create modal ----------
  showCreateModal = false;
  createLoading = false;
  deviceOptions: string[] = [];
  selectedDevice = '';
  /** Per-accordion-row active tab state */
  detailsActiveTab: Record<number, 'maintenance' | 'xbee'> = {};
  private subscription = new Subscription();

  // field groups (for templating all fields + their timestamps)
  readonly maintenanceKeys = ['pcb','antenna','xbee','sim','device_case','cover','bracket'] as const;

  readonly xbeeMainKeys = [
    'network_technology','access_point','airplane_mode','carrier',
    'api_enable','baud_rate','packetization_timeout','flow_control','parity','stop_bits'
  ] as const;

  readonly dioPins = Array.from({length: 15}, (_, i) => `DIO${i}`) as Array<
    'DIO0'|'DIO1'|'DIO2'|'DIO3'|'DIO4'|'DIO5'|'DIO6'|'DIO7'|'DIO8'|'DIO9'|'DIO10'|'DIO11'|'DIO12'|'DIO13'|'DIO14'
  >;

  readonly xbeeTailKeys = ['pr','pd','m0','m1'] as const;

  constructor(
    private fb: FormBuilder,
    private maintenanceApi: MaintenanceService,
    public router: Router,
    private route: ActivatedRoute,
    public theme: ThemeService,
    private cognitoService: CognitoService
  ) {}



  /** Set active tab for a given row */
  setDetailsTab(rowIndex: number, tab: 'maintenance' | 'xbee') {
    this.detailsActiveTab[rowIndex] = tab;
  }

  /** Is tab active for a given row? */
  isDetailsTab(rowIndex: number, tab: 'maintenance' | 'xbee'): boolean {
    return (this.detailsActiveTab[rowIndex] ?? 'maintenance') === tab;
  }


  ngOnInit(): void {
        //Verifies that the current User is allowed to view this component
        this.cognitoService.getCurrentRole(
          [environment.users.role.administrator],
          [environment.users.superAdmin]
        ); //Role Check
    this.loadLatest();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  // ---------- data ----------
  loadLatest(): void {
    this.loading = true;
    this.errorMsg = '';
    const s = this.maintenanceApi.getLatestForAllThings().subscribe({
      next: rows => { this.loading = false; this.latestLogsSubject.next(rows); },
      error: err => { this.loading = false; this.errorMsg = err?.error?.message || err.message || 'Failed to load logs'; }
    });
    this.subscription.add(s);
  }

  // ---------- sort / filter ----------
  toggleSort(key: SortKey): void {
    if (this.sortKeySubject.value === key) {
      this.sortDirSubject.next(this.sortDirSubject.value === 'asc' ? 'desc' : 'asc');
    } else {
      this.sortKeySubject.next(key);
      this.sortDirSubject.next('asc');
    }
  }

  clearFilters(): void {
    this.filterForm.reset({ thingQuery: '', fromEpoch: null, toEpoch: null }, { emitEvent: true });
  }

  // ---------- details modal ----------
  openDetails(thingName: string) {
    this.showDetailsModal = true;
    this.detailsThingName = thingName;
    this.detailsLoading = true;
    this.maintenanceApi.getAllByThingName(thingName).subscribe({
      next: logs => {
        this.detailsLogs = logs;
        this.detailsActiveTab = {};
        logs.forEach((_, idx) => (this.detailsActiveTab[idx] = 'maintenance')); // default tab
        this.detailsLoading = false;
      },
      error: () => { this.detailsLoading = false; }
    });
  }

  closeDetails() {
    this.showDetailsModal = false;
    this.detailsActiveTab = {};
  }


  // ---------- create modal ----------
  async openCreate(): Promise<void> {
    this.showCreateModal = true;
    this.createLoading = true;
    this.deviceOptions = [];
    try {
      const devices = await this.maintenanceApi.getDevices();
      this.deviceOptions = Array.isArray(devices)
        ? devices.map((d: any) => (typeof d === 'string' ? d : d.thing_name)).filter(Boolean)
        : [];
    } catch (e: any) {
      this.errorMsg = e?.error?.message || e.message || 'Failed to load devices';
    } finally {
      this.createLoading = false;
    }
  }
  closeCreate(): void {
    this.showCreateModal = false;
    this.selectedDevice = '';
  }
  routeToCreate(): void {
    // If your select returns an object like { thing_name: 'MWS-B01-013' }, normalize it:
    const thing =this.selectedDevice;

    if (!thing) return;

    this.closeCreate();

    // Absolute navigation (no relativeTo), clean segments
    this.router.navigate(['/maintenance-log', encodeURIComponent(thing)]);
    // or: this.router.navigateByUrl(`/maintenance-log/${encodeURIComponent(thing)}`);
  }


  // ---------- utils ----------
  formatEpochLocal(epochSeconds?: number | null): string {
    if (epochSeconds == null || isNaN(Number(epochSeconds))) return '';
    const d = new Date(Number(epochSeconds) * 1000);

    const yyyy = d.getFullYear();
    const mm   = String(d.getMonth() + 1).padStart(2, '0');
    const dd   = String(d.getDate()).padStart(2, '0');
    const hh   = String(d.getHours()).padStart(2, '0');      // 24-hour
    const mi   = String(d.getMinutes()).padStart(2, '0');
    const ss   = String(d.getSeconds()).padStart(2, '0');

    return `${yyyy}/${mm}/${dd}, ${hh}:${mi}:${ss}`;
  }

  labelize(key: string): string {
    return key.replace(/_/g, ' ').toUpperCase();
  }

  // --- template helpers: avoid (log as any)[key] in HTML ---

getValue(log: JoinedLog, key: string): string {
  const v = (log as any)?.[key];
  return (v ?? '') as string;
}

getTs(log: JoinedLog, tsKey: string): number | null {
  const v = (log as any)?.[tsKey];
  if (typeof v === 'number') return v;
  if (typeof v === 'string' && /^\d+$/.test(v)) return Number(v);
  return null;
}

getXbeeMainTsKey(key: string): string {
  return key === 'baud_rate' ? 'baud_timestamp' : `${key}_timestamp`;
}

// For *ngFor="let key of maintenanceKeys; trackBy: trackByKey"
trackByKey(index: number, key: string): string {
  return key;
}

// For *ngFor="let log of detailsLogs; let i = index; trackBy: trackByIndex"
trackByIndexModal(index: number, _item: unknown): number {
  return index;
}

// --- Note modal state ---
showNoteModal = false;
noteTitle = '';
noteText = '';

openNote(title: string, value: string) {
  this.noteTitle = title;
  this.noteText = (value ?? '').toString();
  this.showNoteModal = true;
}

closeNote() {
  this.showNoteModal = false;
  this.noteTitle = '';
  this.noteText = '';
}

// (optional) robust getters if you don’t have them already
getValueModal(log: JoinedLog, key: string): string {
  const v = (log as any)[key];
  return v == null ? '' : String(v);
}
getTsModal(log: JoinedLog, key: string): number | null {
  const v = (log as any)[key];
  return typeof v === 'number' ? v : (typeof v === 'string' && /^\d+$/.test(v) ? Number(v) : null);
}
getXbeeMainTsKeyModal(key: string): string {
  return key === 'baud_rate' ? 'baud_timestamp' : `${key}_timestamp`;
}
labelizeModal(key: string): string {
  return key.replace(/_/g, ' ').toUpperCase();
}


trackByThing = (_: number, r: JoinedLog) => r.thing_name;
trackByIndex = (i: number) => i;


}
